import React from 'react';
import { Grid, MenuItem, TextField, Alert, Box } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SettingsIcon from '@mui/icons-material/Settings';
import { Controller, useWatch } from 'react-hook-form';
import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import BankAccountAutocomplete from '../BankAccountAutocomplete';
import { PaymentMethodTypeOptions } from '../../Types/invoicesLabels';
import { PaymentMethodType, InvoiceType } from '../../Types/invoicesTypes';
import type { InvoiceDocumentSectionProps } from './InvoiceDocumentSection.types';
import { parseApiDate, formatApiDate, today } from '../../../Shared/Helpers/dateHelpers';
import { invoiceLogger } from '../../utils/invoiceLogger';

const InvoiceDocumentSection: React.FC<InvoiceDocumentSectionProps> = ({
  control,
  errors,
  setValue,
}) => {
  // Watch payment method and invoice type to automatically set paymentDueDate
  const paymentMethod = useWatch({ control, name: 'paymentMethod' });
  const invoiceType = useWatch({ control, name: 'type' });
  const issueDate = useWatch({ control, name: 'issueDate' });
  const companyId = useWatch({ control, name: 'companyId' });
  const currency = useWatch({ control, name: 'currency' });

  // Auto-update paymentDueDate when:
  // 1. Payment method is Cash (already paid)
  // 2. Invoice type is Prepayment (already paid)
  React.useEffect(() => {
    const isCashPayment = paymentMethod === PaymentMethodType.Cash;
    const isPrepayment = invoiceType === InvoiceType.Prepayment;

    if (isCashPayment || isPrepayment) {
      const dueDate = issueDate || today();
      invoiceLogger.debug(
        '[InvoiceDocumentSection] Already paid invoice detected (Cash/Prepayment), setting paymentDueDate to issueDate:',
        dueDate
      );
      setValue('paymentDueDate', dueDate, { shouldValidate: false, shouldDirty: true });
    }
  }, [paymentMethod, invoiceType, issueDate, setValue]);

  return (
    <AccordionCard title="Dane dokumentu" icon={<SettingsIcon color="primary" />}>
      {/* ✅ Alert for cash payments (auto-marked as Paid) */}
      {paymentMethod === PaymentMethodType.Cash && invoiceType !== InvoiceType.Prepayment && (
        <Box mb={2}>
          <Alert severity="success">
            <strong>Płatność gotówką:</strong> Faktura zostanie automatycznie oznaczona jako
            "Opłacona" z datą zapłaty = data wystawienia. Termin płatności zostanie ustawiony na
            datę wystawienia.
          </Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* D A T A   W Y S T A W I E N I A */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="issueDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                <DatePicker
                  label="Data wystawienia"
                  value={parseApiDate(field.value)}
                  onChange={(newValue) => field.onChange(formatApiDate(newValue))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.issueDate,
                      helperText: errors.issueDate?.message as string | undefined,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

        {/* D A T A   E X E C U T I O N   U S Ł U G I */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="saleDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                <DatePicker
                  label="Data wykonania usługi"
                  value={parseApiDate(field.value)}
                  onChange={(newValue) => field.onChange(formatApiDate(newValue))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.saleDate,
                      helperText: errors.saleDate?.message as string | undefined,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

        {/* M I E J S C E   W Y S T A W I E N I A */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="placeOfIssue"
            control={control}
            render={({ field }) => (
              <TextField
                label="Miejsce wystawienia"
                value={field.value || ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                fullWidth
                error={!!errors.placeOfIssue}
                helperText={errors.placeOfIssue?.message as string | undefined}
              />
            )}
          />
        </Grid>

        {/* T E R M I N   P Ł A T N O Ś C I - hidden for Prepayment (already paid) */}
        {invoiceType !== InvoiceType.Prepayment && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="paymentDueDate"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                  <DatePicker
                    label="Termin płatności"
                    value={parseApiDate(field.value)}
                    onChange={(newValue) => field.onChange(formatApiDate(newValue))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.paymentDueDate,
                        helperText: errors.paymentDueDate?.message as string | undefined,
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        )}

        {/* F O R M A   P Ł A T N O Ś C I */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="paymentMethod"
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Forma płatności"
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const normalized =
                    typeof raw === 'string' && /^\d+$/.test(raw)
                      ? Number(raw)
                      : (raw as PaymentMethodType);
                  field.onChange(normalized);
                }}
                onBlur={field.onBlur}
                fullWidth
                error={!!errors.paymentMethod}
                helperText={errors.paymentMethod?.message as string | undefined}
              >
                {PaymentMethodTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* K O N T O   B A N K O W E */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Controller
            name="bankAccount"
            control={control}
            render={({ field }) => (
              <BankAccountAutocomplete
                companyId={typeof companyId === 'number' ? companyId : undefined}
                currency={typeof currency === 'string' ? currency : undefined}
                value={field.value || ''}
                onChange={(val) => field.onChange(val)}
                error={!!errors.bankAccount}
                helperText={errors.bankAccount?.message as string | undefined}
              />
            )}
          />
        </Grid>
      </Grid>
    </AccordionCard>
  );
};;

export default InvoiceDocumentSection;
